<template>
  <div>
    <header class="wsp-toolbar">
      <div class="wsp-toolbar__title">
        <span class="wsp-toolbar__accent" aria-hidden="true"></span>
        <div>
          <h1>Articles</h1>
          <p>Editorial blog posts published on the public site</p>
        </div>
      </div>
      <div class="wsp-toolbar__actions">
        <div class="wsp-search">
          <span class="wsp-search__icon">🔍</span>
          <input v-model.trim="search" type="text" placeholder="Search articles…" />
        </div>
        <button class="wsp-btn-refresh" :class="{ 'is-loading': loading }" :disabled="loading" @click="fetch">
          <svg class="wsp-btn-refresh__icon" viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <polyline points="23 4 23 10 17 10"/><polyline points="1 20 1 14 7 14"/>
            <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/>
          </svg>
          <span>{{ loading ? 'Refreshing…' : 'Refresh' }}</span>
        </button>
        <button class="wsp-btn-primary" @click="openCreate">+ Add Article</button>
      </div>
    </header>

    <CAlert v-if="error" color="danger" show>{{ error }}</CAlert>

    <section class="wsp-card">
      <header class="wsp-card__head">
        <h2>All Articles</h2>
        <span class="wsp-card__count">{{ filtered.length }} item(s)</span>
      </header>

      <div v-if="loading" class="wsp-loading"><CSpinner size="sm" /> Loading…</div>
      <div v-else-if="!filtered.length" class="wsp-empty">
        <div class="wsp-empty__title">No articles found</div>
        <div>Add your first article to get started.</div>
      </div>
      <div v-else>
        <table class="wsp-table">
          <thead>
            <tr>
              <th>Article</th>
              <th>Slug</th>
              <th>Published</th>
              <th class="wsp-actions">Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in filtered" :key="row.id">
              <td>
                <strong>{{ row.title }}</strong>
                <div v-if="row.excerpt" style="font-size:0.75rem;color:#6b7280;max-width:480px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;">
                  {{ row.excerpt }}
                </div>
              </td>
              <td><code style="font-size:0.75rem;color:#0e5b46;">{{ row.slug }}</code></td>
              <td>{{ formatDate(row.published_at || row.created_at) }}</td>
              <td class="wsp-actions">
                <button class="wsp-icon-btn" @click="openEdit(row)" title="Edit">✎</button>
                <button class="wsp-icon-btn wsp-icon-btn--danger" @click="confirmDelete(row)" title="Delete">🗑</button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </section>

    <div v-if="modalOpen" class="wsp-modal-backdrop" @click.self="closeModal">
      <div class="wsp-modal wsp-modal--wide">
        <header class="wsp-modal__head">
          <h3>{{ form.id ? 'Edit Article' : 'Add Article' }}</h3>
          <button class="wsp-modal__close" @click="closeModal">×</button>
        </header>
        <div class="wsp-modal__body">
          <div class="wsp-form-grid">
            <div class="wsp-field">
              <label>Title <span style="color:#b91c1c">*</span></label>
              <input v-model="form.title" type="text" @input="autoSlug" />
              <span v-if="errors.title" class="wsp-field__error">{{ errors.title }}</span>
            </div>
            <div class="wsp-field">
              <label>Slug <span style="color:#b91c1c">*</span></label>
              <input v-model="form.slug" type="text" placeholder="kebab-case" />
              <span v-if="errors.slug" class="wsp-field__error">{{ errors.slug }}</span>
            </div>
            <div class="wsp-field wsp-field--full">
              <label>Excerpt</label>
              <textarea v-model="form.excerpt" rows="2" placeholder="Short description shown in previews"></textarea>
            </div>
            <div class="wsp-field wsp-field--full">
              <label>Content</label>
              <textarea v-model="form.content" rows="10" placeholder="HTML allowed"></textarea>
              <span class="wsp-field__hint">HTML is rendered as-is on the public site.</span>
            </div>
            <div class="wsp-field">
              <label>Image URL</label>
              <input v-model="form.image_url" type="text" placeholder="https://…" />
            </div>
            <div class="wsp-field">
              <label>Published at</label>
              <input v-model="form.published_at" type="datetime-local" />
            </div>
          </div>
        </div>
        <footer class="wsp-modal__foot">
          <button class="wsp-btn-ghost" :disabled="saving" @click="closeModal">Cancel</button>
          <button class="wsp-btn-primary" :disabled="saving" @click="save">
            {{ saving ? 'Saving…' : (form.id ? 'Save changes' : 'Create article') }}
          </button>
        </footer>
      </div>
    </div>
  </div>
</template>

<script>
import websitesApi from '@/api/websites'

const empty = () => ({ id: null, title: '', slug: '', excerpt: '', content: '', image_url: '', published_at: '' })

export default {
  name: 'WebsitesArticles',
  data () {
    return {
      rows: [],
      loading: false,
      error: '',
      search: '',
      modalOpen: false,
      saving: false,
      form: empty(),
      errors: {},
      slugTouched: false,
    }
  },
  computed: {
    filtered () {
      const q = this.search.toLowerCase()
      if (!q) return this.rows
      return this.rows.filter(r =>
        (r.title || '').toLowerCase().includes(q) ||
        (r.slug  || '').toLowerCase().includes(q) ||
        (r.excerpt || '').toLowerCase().includes(q)
      )
    },
  },
  mounted () { this.fetch() },
  methods: {
    async fetch () {
      this.loading = true; this.error = ''
      try { this.rows = await websitesApi.articlesList() }
      catch (e) { this.error = this.extractError(e) }
      finally { this.loading = false }
    },
    openCreate () { this.form = empty(); this.errors = {}; this.slugTouched = false; this.modalOpen = true },
    openEdit (row) {
      this.form = {
        id: row.id,
        title: row.title || '',
        slug: row.slug || '',
        excerpt: row.excerpt || '',
        content: row.content || '',
        image_url: row.image_url || '',
        published_at: this.toLocalDt(row.published_at),
      }
      this.errors = {}; this.slugTouched = true; this.modalOpen = true
    },
    autoSlug () {
      if (this.form.id || this.slugTouched) return
      this.form.slug = this.slugify(this.form.title)
    },
    slugify (s) {
      return (s || '').toLowerCase().trim()
        .replace(/[^a-z0-9\s-]/g, '')
        .replace(/\s+/g, '-')
        .replace(/-+/g, '-')
    },
    closeModal () { if (!this.saving) this.modalOpen = false },
    async save () {
      this.errors = {}
      if (!this.form.title.trim()) { this.errors.title = 'Title is required' }
      if (!this.form.slug.trim())  { this.errors.slug  = 'Slug is required' }
      if (Object.keys(this.errors).length) return
      this.saving = true
      try {
        const payload = {
          title: this.form.title,
          slug: this.form.slug,
          excerpt: this.form.excerpt,
          content: this.form.content,
          image_url: this.form.image_url || null,
          published_at: this.form.published_at ? new Date(this.form.published_at).toISOString() : null,
        }
        if (this.form.id) await websitesApi.articleUpdate(this.form.id, payload)
        else              await websitesApi.articleCreate(payload)
        this.modalOpen = false
        await this.fetch()
      } catch (e) {
        if (e && e.response && e.response.status === 409) this.errors.slug = 'Slug already exists'
        else this.error = this.extractError(e)
      } finally { this.saving = false }
    },
    async confirmDelete (row) {
      if (!confirm(`Delete article "${row.title}"?`)) return
      try { await websitesApi.articleDelete(row.id); await this.fetch() }
      catch (e) { this.error = this.extractError(e) }
    },
    formatDate (d) { if (!d) return '—'; const dt = new Date(d); return dt.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) },
    toLocalDt (iso) {
      if (!iso) return ''
      const d = new Date(iso); if (isNaN(d)) return ''
      const pad = n => String(n).padStart(2, '0')
      return `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
    },
    extractError (e) {
      return (e && e.response && e.response.data && (e.response.data.error || e.response.data.message))
        || (e && e.message) || 'Request failed'
    },
  },
  watch: {
    'form.slug' () { if (this.modalOpen) this.slugTouched = true },
  },
}
</script>
